import { InternalAxiosRequestConfig } from "axios";
import { getPracticeCustomerName, getPracticeScenario } from "./practiceMode";
import { practiceSupplierName, practiceSupplierPhone, practiceSupplierTaxId } from "./practiceMerchant";

/**
 * 練習模式的「假下單後端」（有狀態）。
 * --------------------------------------------------------------------------
 * gasShop 與 kiosk 兩條下單流程共用同一組 platform-order-drafts / platform-orders 端點，
 * 因此這裡用一份 module 級的草稿狀態，模擬「建草稿 → 加品項 → 算費 → 送出」整條流程，
 * 讓客服能在練習模式一路點到「訂單完成」。每次重新開始情境時呼叫 resetPracticeOrder() 清空。
 */

const SUPPLIER_ID = "PRACTICE-SUP-001";
const ADDRESS_ID = "PRACTICE-ADDR-001";
const ADDRESS_FULL = "台北市信義區市府路45號";
const DRAFT_ID = "PRACTICE-DRAFT-001";
const ORDER_ID = "PRACTICE-ORDER-001";
const ORDER_NO = "PO-PRACTICE-001";

/** 取未來幾天的日期字串（YYYY-MM-DD），給配送時段用（避免被「過去時段」過濾掉）。 */
const futureDate = (addDays: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + addDays);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

// price_range：平台後台設定的參考售價區間（元／桶，純顯示用）。
// 練習站同時是 TapPay 商店審核用網址，/practice 首頁的「商店資訊」會列出每個瓶種的
// 價格範圍，因此每個瓶種都要有區間；同時保留「區間 / 單一數字（min===max）」兩種呈現。
const GAS_PRODUCTS = [
  { gas_id: 1, price_id: 101, gas_type: "瓦斯", kilogram: 20, unit_price: 700, type: "gas", price_range: { min: 700, max: 760 } },
  { gas_id: 2, price_id: 102, gas_type: "瓦斯", kilogram: 10, unit_price: 400, type: "gas", price_range: { min: 400, max: 400 } },
  { gas_id: 3, price_id: 103, gas_type: "瓦斯", kilogram: 50, unit_price: 1600, type: "gas", price_range: { min: 1600, max: 1700 } },
];

/** /practice 首頁「商店資訊」用：瓶種與參考售價區間（與 bootstrap 同一份資料，依瓶重由小到大）。 */
export const PRACTICE_GAS_PRICE_RANGES = [...GAS_PRODUCTS]
  .sort((a, b) => a.kilogram - b.kilogram)
  .map(({ kilogram, price_range }) => ({ kilogram, price_range }));

const COMMODITIES = [{ commodity_id: 1, price_id: 201, commodity_type: "soft_tube", display_name: "瓦斯軟管 1M", unit_price: 150, type: "commodity" }];

const INSTALL_LOCATIONS = [
  { customer_delivery_id: 1, customer_address_id: ADDRESS_ID, usage_location: "瓦斯爐", note: "廚房", floor: 2, is_elevator: true },
  { customer_delivery_id: 2, customer_address_id: ADDRESS_ID, usage_location: "熱水器", note: "陽台", floor: 2, is_elevator: true },
];

const deliverySlots = () => [
  {
    delivery_date: futureDate(1),
    is_closed_all_day: false,
    slots: [
      { slot_id: 1001, slot_source: "internal", start_time: "09:00", end_time: "12:00", available_capacity: 10 },
      { slot_id: 1002, slot_source: "internal", start_time: "14:00", end_time: "17:00", available_capacity: 8 },
    ],
  },
  {
    delivery_date: futureDate(2),
    is_closed_all_day: false,
    slots: [{ slot_id: 1003, slot_source: "internal", start_time: "10:00", end_time: "13:00", available_capacity: 6 }],
  },
];

const lastOrder = () => ({
  gas_list: [
    {
      type: "gas",
      price_id: 101,
      gas_id: 1,
      gas_type: "瓦斯",
      kilogram: 20,
      unit_price: 700,
      qty: 1,
      install_location: INSTALL_LOCATIONS[0],
    },
  ],
});

export const PRACTICE_SUPPLIER_ID = SUPPLIER_ID;
export const PRACTICE_ADDRESS_ID = ADDRESS_ID;
export const PRACTICE_ADDRESS_FULL = ADDRESS_FULL;

/** bootstrap 回傳：供應商（含商品/時段/上次訂單）＋地址清單。 */
const buildBootstrap = () => ({
  available_suppliers: [
    {
      supplier_id: SUPPLIER_ID,
      supplier_name: practiceSupplierName(),
      tax_id_number: practiceSupplierTaxId(),
      phone: practiceSupplierPhone(),
      is_open_now: true,
      allow_customer_reservation: true,
      gas_cylinders: GAS_PRODUCTS,
      commodities: COMMODITIES,
      delivery_slots: deliverySlots(),
      last_order: lastOrder(),
      // enable_electronic_payment：練習站同時是 TapPay 商店審核網址，開啟讓結帳流程出現
      // 「怎麼付款？」步驟（已綁卡／貨到付款／新增信用卡＋TapPay 安全揭露）；假後端已有 payment-methods 假資料。
      platform_setting: { show_invoice: true, enable_electronic_payment: true },
      customer_type: null,
      mortgage_kilograms: [10, 20, 50],
    },
  ],
  available_addresses: [
    {
      customer_address_id: ADDRESS_ID,
      full_address: ADDRESS_FULL,
      city: "台北市",
      district: "信義區",
      address: "市府路45號",
      install_locations: INSTALL_LOCATIONS,
    },
  ],
});

// --- 有狀態的草稿 --------------------------------------------------------

interface DraftItem {
  line_id: string;
  [key: string]: unknown;
}

/**
 * 練習沙盒是否已填過用氣習慣問卷。
 * 首頁送出問卷後會重新抓 /customer/home；若 survey_prompt 永遠回 needed:true，
 * 剛填完的問卷卡會馬上又長回來。POST /customer/gas-survey 時把這個旗標打開。
 */
/** 存 sessionStorage 而非只用模組變數：客服在首頁重新整理時，模組變數會歸零，
 *  問卷卡就會又冒出來（真實後端是持久化的）。 */
const SURVEY_DONE_KEY = "practiceSurveyDone";
const practiceSurveyDone = () => {
  try {
    return sessionStorage.getItem(SURVEY_DONE_KEY) === "1";
  } catch {
    return false;
  }
};
export const markPracticeSurveyDone = () => {
  try {
    sessionStorage.setItem(SURVEY_DONE_KEY, "1");
  } catch {
    /* 私密模式等寫入失敗時忽略 */
  }
};
/** 重新開始情境時呼叫（不放在 resetPracticeOrder，避免下單完成的重置也一併清掉）。 */
export const resetPracticeSurvey = () => {
  try {
    sessionStorage.removeItem(SURVEY_DONE_KEY);
  } catch {
    /* 同上 */
  }
};

let gasList: DraftItem[] = [];
let commodityList: DraftItem[] = [];
let lineSeq = 1;
let draftMeta: Record<string, unknown> = {};

/**
 * 練習草稿狀態持久化。
 * --------------------------------------------------------------------------
 * 購物車（useOrder.productList）會 persist 到 sessionStorage，但這份 module 級的
 * 草稿狀態原本只存在記憶體。整頁 reload（LIFF / webview 很常見）時 module 狀態歸零、
 * 購物車卻還在 → 兩者不同步，下一次 GET /platform-order-drafts/current 因 gasList 為空
 * 回傳 draft: null，useCurrentDraftSync 便 clearProducts() 把購物車清掉，使用者在
 * 「配送時間 → 結帳」途中被踢回「瓦斯訂購」頁。把草稿狀態也 persist，reload 後還原，
 * 與購物車保持同步即可避免被踢回。
 */
const PERSIST_KEY = "practiceOrderState";

const persistOrderState = () => {
  try {
    sessionStorage.setItem(PERSIST_KEY, JSON.stringify({ gasList, commodityList, lineSeq, draftMeta }));
  } catch {
    // sessionStorage 不可用（隱私模式等）→ 靜默退回純記憶體行為
  }
};

// module 載入時還原上次的草稿狀態（reload 後與購物車對齊）
(() => {
  try {
    const raw = sessionStorage.getItem(PERSIST_KEY);
    if (!raw) return;
    const s = JSON.parse(raw);
    gasList = Array.isArray(s.gasList) ? s.gasList : [];
    commodityList = Array.isArray(s.commodityList) ? s.commodityList : [];
    lineSeq = typeof s.lineSeq === "number" ? s.lineSeq : 1;
    draftMeta = s.draftMeta && typeof s.draftMeta === "object" ? s.draftMeta : {};
  } catch {
    // 解析失敗 → 維持空白起始狀態
  }
})();

export const resetPracticeOrder = () => {
  gasList = [];
  commodityList = [];
  lineSeq = 1;
  draftMeta = {};
  persistOrderState();
};

/**
 * 預載一筆瓦斯到草稿（等同「自動帶入上次訂單」，但同步、可靠）。
 * 回傳 draft 物件，呼叫端可 syncDraft 進 useOrder，確保購物車一進去就有品項。
 */
export const seedGasShopOrder = () => {
  resetPracticeOrder();
  addItem("gas", {
    type: "gas",
    price_id: 101,
    gas_id: 1,
    gas_type: "瓦斯",
    kilogram: 20,
    unit_price: 700,
    qty: 1,
    usage_location: "瓦斯爐",
    note: "廚房",
    floor: 2,
    is_elevator: true,
    customer_delivery_id: 1,
  });
  return buildDraft();
};

/**
 * 下單情境（瓦斯訂購 / 點餐機）保底：草稿意外變空時補回示範品項。
 * 結帳流程中若草稿在某個環節被清空（實測有偶發去同步），購物車/結算頁會因「空車」把客服
 * 踢回商品頁形成迴圈。只在下單情境、且草稿確實空了時補一筆，避免迴圈。
 * （讀 sessionStorage 判斷情境，避免與 practiceMode 產生 import 循環。）
 */
const ensureOrderSeed = () => {
  if (gasList.length || commodityList.length) return;
  let scenario: string | null = null;
  try {
    scenario = sessionStorage.getItem("practiceScenario");
  } catch {
    // sessionStorage 不可用 → 不補
  }
  if (scenario === "orderGasShop" || scenario === "orderKiosk") seedGasShopOrder();
};

const priceFor = (item: Record<string, any>): number => {
  if (typeof item.unit_price === "number") return item.unit_price;
  const found = [...GAS_PRODUCTS, ...COMMODITIES].find((p) => p.price_id === item.price_id);
  return found?.unit_price ?? 0;
};

const newLineId = () => `PRACTICE-LINE-${lineSeq++}`;

const addItem = (type: "gas" | "commodity", raw: Record<string, any>) => {
  const item: DraftItem = { ...raw, line_id: newLineId(), qty: raw.qty ?? 1, unit_price: priceFor(raw) };
  if (type === "gas") gasList.push(item);
  else commodityList.push(item);
  persistOrderState();
};

const updateItem = (type: "gas" | "commodity", lineId: string, raw: Record<string, any>) => {
  const list = type === "gas" ? gasList : commodityList;
  const idx = list.findIndex((i) => i.line_id === lineId);
  if (idx >= 0) list[idx] = { ...list[idx], ...raw, line_id: lineId, unit_price: priceFor({ ...list[idx], ...raw }) };
  persistOrderState();
};

const delItem = (type: "gas" | "commodity", lineId: string) => {
  if (type === "gas") gasList = gasList.filter((i) => i.line_id !== lineId);
  else commodityList = commodityList.filter((i) => i.line_id !== lineId);
  persistOrderState();
};

const buildDraft = () => ({
  draft_id: DRAFT_ID,
  supplier_id: SUPPLIER_ID,
  customer_address_id: ADDRESS_ID,
  gas_list: gasList,
  commodity_list: commodityList,
  contact_phone: (draftMeta.contact_phone as string) ?? null,
  payment_method_ref: (draftMeta.payment_method_ref as string) ?? null,
  invoice_type: (draftMeta.invoice_type as string) ?? null,
  invoice_carrier: (draftMeta.invoice_carrier as string) ?? null,
  invoice_tax_id: (draftMeta.invoice_tax_id as string) ?? null,
  delivery_type: (draftMeta.delivery_mode as string) ?? null,
  delivery_date: (draftMeta.delivery_date as string) ?? null,
  delivery_slot_id: (draftMeta.delivery_slot_id as number) ?? null,
  delivery_slot_source: (draftMeta.delivery_slot_source as string) ?? null,
  delivery_slot_label: (draftMeta.delivery_slot_label as string) ?? null,
  customer_coupon_id: (draftMeta.customer_coupon_id as string) ?? null,
});

/** 目前草稿掛的那張券實際能折多少（練習模式的簡化版：可用、達門檻才折，且不超過商品小計）。 */
const couponDiscountOf = (subtotal: number): number => {
  const id = draftMeta.customer_coupon_id as string | null | undefined;
  if (!id) return 0;
  const c = practiceCoupons().find((x) => x.customer_coupon_id === id);
  // 已用 / 已過期的券不折。這條在真後端是權威判斷，練習模式照著做，
  // 否則畫面會出現「掛著一張券卻沒扣錢」的對不起來。
  if (!c || c.status !== "available") return 0;
  if (subtotal < (c.min_order_amount ?? 0)) return 0;
  return Math.min(c.discount_value, subtotal);
};

const buildPreview = () => {
  const items = [...gasList, ...commodityList];
  const subtotal = items.reduce((s, i) => s + Number(i.unit_price || 0) * Number(i.qty || 1), 0);
  const fee = items.length ? 50 : 0;
  const floor_fee = gasList.length ? 30 : 0;
  const coupon_discount = couponDiscountOf(subtotal);
  const total = subtotal + fee + floor_fee - coupon_discount;
  return {
    subtotal,
    fee,
    floor_fee,
    platform_service_fee: 0,
    coupon_discount,
    total,
    gas_items: gasList.map((_, index) => ({ index, is_available: true, price_changed: null, floor_fee_unit_price: 30 })),
    commodity_items: commodityList.map((_, index) => ({ index, is_available: true })),
    delivery_slot_available: true,
    requires_auth: false,
  };
};

/** 送出當下的訂單快照。
 *
 *  下單完成頁會用剛拿到的 order_id 去 GET /platform-orders/:id。若那支一律回下面那份
 *  示範訂單，客服就會看到「剛才確認的是 780、送出後卻變成 870」——同一條流程裡金額對不起來，
 *  正是練習模式最不該給的印象。送出時把當下的品項與金額存起來，完成頁就拿得到自己的數字；
 *  其他訂單（訂單列表點進去的）仍走下面那份較豐富的示範資料。 */
let submittedSnapshot: ReturnType<typeof buildPreview> | null = null;
let submittedItems: DraftItem[] = [];
/** 送出當下的配送方式（草稿 draftMeta 在送出後會被清空）：完成頁點「查看訂單」要顯示客服剛選的立即／預約與時段，
 *  不能沿用示範訂單寫死的「預約 09:00 - 12:00」。 */
let submittedDelivery: { mode: string | null; date: string | null; slotId: number | null } | null = null;

/** 依送出當下的配送方式組出訂單詳情 order_list 的配送欄位；沒有快照時回示範訂單的預約時段。 */
const deliveryFieldsFor = (d: typeof submittedDelivery) => {
  if (!d) {
    return { delivery_type: "預約配送", delivery_time_stamp: futureDate(1) + "T12:00:00Z", delivery_slot_label: "09:00 - 12:00" };
  }
  if (d.mode !== "reserve") {
    return { delivery_type: "立即配送", delivery_time_stamp: futureDate(0) + "T12:00:00Z", delivery_slot_label: "" };
  }
  const slot = deliverySlots()
    .flatMap((day) => day.slots)
    .find((sl) => sl.slot_id === d.slotId);
  return {
    delivery_type: "預約配送",
    delivery_time_stamp: (d.date || futureDate(1)) + "T12:00:00Z",
    delivery_slot_label: slot ? `${slot.start_time} - ${slot.end_time}` : "",
  };
};

/** 訂單詳情（GET /platform-orders/:id）：自包含的完整訂單，不依賴會被送單清空的草稿 gasList，
 *  讓練習模式點任何一筆訂單都看得到完整內容（品項/配送時間/電話/樓層費/優惠券折抵/摘要/實付）。 */
const buildOrderDetail = (snap: ReturnType<typeof buildPreview> | null = null) => ({
  platform_order_id: ORDER_ID,
  order_status: "delivering",
  create_time_stamp: new Date().toISOString(),
  supplier_id: SUPPLIER_ID,
  supplier: { supplier_id: SUPPLIER_ID, supplier_name: practiceSupplierName() },
  supplier_order_snapshot_json: {
    order_list: {
      order_id: ORDER_NO,
      supplier_name: practiceSupplierName(),
      customer_phone: "0912345678",
      ...deliveryFieldsFor(snap ? submittedDelivery : null),
      floor_fee: snap ? snap.floor_fee : 50,
      floor_fee_discount_amount: 0,
      discount: 0,
      gas_discount: 0,
      coupon_supplier_discount_amount: snap ? 0 : 30,
      // 沒有快照時是這份示範訂單：700(瓦斯) + 150(軟管) + 50(樓層費) − 30(優惠券)
      total_price_cached: snap ? snap.total : 870,
      gas_list: snap ? submittedItems.filter((i) => i.type !== "commodity") : gasList,
      commodity_list: snap ? submittedItems.filter((i) => i.type === "commodity") : commodityList,
    },
    // 訂單詳細頁品項/摘要讀的是 items（非 order_list.gas_list）
    items: snap
      ? submittedItems.map((raw) => {
          // DraftItem 是 { line_id, [key: string]: unknown }，取值前先收斂型別
          const i = raw as unknown as {
            type?: string; display_name?: string; gas_type?: string;
            kilogram?: number; qty: number; unit_price: number;
            note?: string; usage_location?: string;
          };
          // 括號不能拿掉：`return` 後直接換行會被自動補分號，整個品項變成 undefined，訂單詳情讀 subtotal 就崩
          return (
            i.type === "commodity"
              ? { kind: "commodity", commodity_name: i.display_name || "商品", numbers_of_commodity: i.qty, unit_price: i.unit_price, subtotal: i.unit_price * i.qty }
              : {
                  kind: "gas", gas_type: i.gas_type, gas_kilogram: i.kilogram, numbers_of_cylinder: i.qty,
                  unit_price: i.unit_price, subtotal: i.unit_price * i.qty,
                  delivery_location: [i.note, i.usage_location].filter(Boolean).join(" · "),
                }
          );
        })
      : [
          { kind: "gas", gas_type: "瓦斯", gas_kilogram: 20, numbers_of_cylinder: 1, unit_price: 700, subtotal: 700, delivery_location: "廚房 · 瓦斯爐" },
          { kind: "commodity", commodity_name: "瓦斯軟管 1M", numbers_of_commodity: 1, unit_price: 150, subtotal: 150 },
        ],
    cylinders: [],
    mortgages: [],
    refunds: [],
    usage_fees: [],
    // 實付明細（配送中訂單多為貨到付款，先留空；完成訂單可帶入以顯示「實付」）
    payups: [],
  },
  address_snapshot_json: { full_address: ADDRESS_FULL, latitude: DEST.lat, longitude: DEST.lng },
  payer_snapshot_json: { name: "練習客戶", phone: "0912345678" },
});

/** 練習用優惠券（可用 2 張 + 已用/過期各 1，讓優惠券頁各分頁都不空）。 */
const practiceCoupons = () => [
  {
    customer_coupon_id: "coup-001",
    coupon_id: "cpn-001",
    coupon_name: "新客折抵 $50",
    coupon_code: "NEW50",
    coupon_type: "discount",
    discount_value: 50,
    min_order_amount: 200,
    applicable_items: "all",
    supplier_id: null,
    status: "available",
    received_method: "platform",
    received_time_stamp: futureDate(-30) + "T00:00:00Z",
    expire_time_stamp: futureDate(60) + "T23:59:59Z",
    used_time_stamp: null,
    used_platform_order_id: null,
  },
  {
    customer_coupon_id: "coup-002",
    coupon_id: "cpn-002",
    coupon_name: practiceSupplierName() + " $30 折抵",
    coupon_code: "PRACTICE30",
    coupon_type: "discount",
    discount_value: 30,
    min_order_amount: 300,
    applicable_items: "gas",
    supplier_id: SUPPLIER_ID,
    status: "available",
    received_method: "account",
    received_time_stamp: futureDate(-20) + "T00:00:00Z",
    expire_time_stamp: futureDate(40) + "T23:59:59Z",
    used_time_stamp: null,
    used_platform_order_id: null,
  },
  {
    customer_coupon_id: "coup-003",
    coupon_id: "cpn-003",
    coupon_name: "已使用 $20 折抵",
    coupon_code: "USED20",
    coupon_type: "discount",
    discount_value: 20,
    min_order_amount: 100,
    applicable_items: "all",
    supplier_id: null,
    status: "used",
    received_method: "platform",
    received_time_stamp: futureDate(-40) + "T00:00:00Z",
    expire_time_stamp: futureDate(20) + "T23:59:59Z",
    used_time_stamp: futureDate(-5) + "T14:30:00Z",
    used_platform_order_id: ORDER_ID,
  },
  {
    customer_coupon_id: "coup-004",
    coupon_id: "cpn-004",
    coupon_name: "已過期 $100 折抵",
    coupon_code: "EXPIRED100",
    coupon_type: "discount",
    discount_value: 100,
    min_order_amount: 500,
    applicable_items: "all",
    supplier_id: null,
    status: "expired",
    received_method: "platform",
    received_time_stamp: futureDate(-90) + "T00:00:00Z",
    expire_time_stamp: futureDate(-10) + "T23:59:59Z",
    used_time_stamp: null,
    used_platform_order_id: null,
  },
];

/** 訂單列表（/orders 頁）。欄位對齊訂單卡：owner/status/items_summary/total/display_id/created_at。 */
const buildOrderList = () => [
  {
    platform_order_id: "po-001",
    display_id: "ADO_8805",
    owner: { context_id: 9001, label: "練習家庭帳戶", context_name: "練習家庭帳戶" },
    supplier: { supplier_id: SUPPLIER_ID, supplier_name: practiceSupplierName() },
    status: "delivering",
    status_label: "配送中",
    items_summary: [{ pn: "20kg", qty: 1 }],
    total: 730,
    payment_method_label: "貨到付款",
    created_at: new Date().toISOString(),
  },
  {
    platform_order_id: "po-002",
    display_id: "ADO_8804",
    owner: { context_id: 9001, label: "練習家庭帳戶", context_name: "練習家庭帳戶" },
    supplier: { supplier_id: SUPPLIER_ID, supplier_name: practiceSupplierName() },
    status: "completed",
    status_label: "已完成",
    items_summary: [{ pn: "50kg", qty: 1 }],
    total: 1600,
    payment_method_label: "信用卡",
    created_at: new Date(Date.now() - 6 * 864e5).toISOString(),
  },
];

// --- 首頁叫桶預測（區間預測 v2）------------------------------------------

/**
 * 既有客戶的預測資料（存量充足）：上次叫桶 16 天前，預估用完窗還有 9~17 天。
 * 一般練習情境（下單／查訂單／帳號）用這組。
 */
const gasStatusHealthy = () => ({
  prediction_status: "可預測",
  prediction_source: "model",
  auto_eligible: true,
  notify_tier: "auto",
  predicted_days: 28,
  last_order_date: futureDate(-16),
  suggested_delivery_date: futureDate(12),
  depletion_low: futureDate(9),
  depletion_high: futureDate(17),
  dispatch_lower_date: futureDate(9),
  observe_upper_date: futureDate(17),
  notify_standard_date: futureDate(7),
});

/**
 * 「瓦斯快用完」的預測資料：上次叫桶 26 天前、預估 2~6 天內用完、提醒日已過。
 * 供 LINE 到期通知情境（notifyExpiring）使用——通知卡片與首頁讀同一組數字，
 * 客服練習時兩邊看到的日期才會一致。
 */
export const practiceGasStatusExpiring = () => ({
  prediction_status: "可預測",
  prediction_source: "model",
  auto_eligible: true,
  notify_tier: "auto",
  predicted_days: 28,
  last_order_date: futureDate(-26),
  suggested_delivery_date: futureDate(2),
  depletion_low: futureDate(2),
  depletion_high: futureDate(6),
  dispatch_lower_date: futureDate(2),
  observe_upper_date: futureDate(6),
  notify_standard_date: futureDate(-1),
});

// --- 請求分派 ------------------------------------------------------------

const lastSeg = (u: string) => u.split("?")[0].split("/").filter(Boolean).pop() || "";

const parseBody = (config: InternalAxiosRequestConfig): Record<string, any> => {
  try {
    return typeof config.data === "string" ? JSON.parse(config.data) : config.data || {};
  } catch {
    return {};
  }
};

/**
 * 處理下單相關端點，回傳要放進 response.data 的物件；非下單端點回 null（交回上層處理）。
 */

/* ==========================================================================
 * 配送員即時定位的練習模擬（GET /platform-orders/:id/tracking）
 * ==========================================================================
 * 練習模式的示範訂單本來就是「配送中」，缺的只是一台會動的車。這裡用一條固定
 * 路線在三分鐘內跑完並循環，讓整個狀態序列都看得到：
 *   前面還有 2 單 → 前面還有 1 單 → 前往中 → 即將抵達 → 已抵達附近（步行搬運）
 * 每一段對應 trackingDisplay 的一條規則，所以這支模擬同時也是那些文案的展示台。
 *
 * ETA 刻意在這裡（＝「後端」）算完才回傳，與正式版的分工一致：
 * 前端只知道直線距離，讓它自己估會系統性偏樂觀，而且改參數就得重發 LIFF。
 */

/** 配送地址（台北市政府一帶）。同時給地址快照與追蹤模擬使用。 */
const DEST = { lat: 25.0375, lng: 121.5637 };

/** 配送員的路線：由西北方約 3.5 公里外一路開到配送地址。折點是為了讓車看起來會轉彎。 */
const TRACK_ROUTE = [
  { lat: 25.0605, lng: 121.5400 },
  { lat: 25.0520, lng: 121.5450 },
  { lat: 25.0450, lng: 121.5540 },
  { lat: 25.0400, lng: 121.5610 },
  DEST,
];

/** 跑完整條路線的時間。三分鐘：夠看完所有狀態，又不必等到失去耐性。 */
const TRACK_CYCLE_MS = 3 * 60 * 1000;

/** 第一次查詢的時間點。以此為起點推進，重整頁面不會把車重設回起點。 */
let trackingStartedAt: number | null = null;

const metersBetween = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

const bearingOf = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const y = Math.sin(toRad(b.lng - a.lng)) * Math.cos(toRad(b.lat));
  const x =
    Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) -
    Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(toRad(b.lng - a.lng));
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

/** 依累積距離取路線上的點，車速才會是固定的（依折點平均分配會讓長段落開得比較快）。 */
const pointAlongRoute = (progress: number) => {
  const legs = TRACK_ROUTE.slice(1).map((p, i) => metersBetween(TRACK_ROUTE[i], p));
  const total = legs.reduce((a, b) => a + b, 0);
  let want = Math.min(1, Math.max(0, progress)) * total;
  for (let i = 0; i < legs.length; i++) {
    if (want > legs[i] && i < legs.length - 1) {
      want -= legs[i];
      continue;
    }
    const t = legs[i] === 0 ? 1 : Math.min(1, want / legs[i]);
    const from = TRACK_ROUTE[i];
    const to = TRACK_ROUTE[i + 1];
    return {
      lat: from.lat + (to.lat - from.lat) * t,
      lng: from.lng + (to.lng - from.lng) * t,
      bearing: bearingOf(from, to),
    };
  }
  return { ...DEST, bearing: 0 };
};

const buildTracking = () => {
  if (trackingStartedAt === null) trackingStartedAt = Date.now();
  const progress = ((Date.now() - trackingStartedAt) % TRACK_CYCLE_MS) / TRACK_CYCLE_MS;
  const { lat, lng, bearing } = pointAlongRoute(progress);
  const remaining = metersBetween({ lat, lng }, DEST);

  // 最後一段改成步行：示範「已下車、正在搬運」那個狀態
  const onFoot = progress > 0.93;
  // 前面還有幾單：只回數量，不帶其他訂單的任何內容（正式版也必須如此）
  const stopsAhead = progress < 0.3 ? 2 : progress < 0.5 ? 1 : 0;

  // 市區均速約 25 km/h，另外每一個前面的停靠點抓 6 分鐘（卸貨、爬樓梯、收款）。
  // 一律往壞的估：早到是驚喜，晚到是客訴。
  const driveMinutes = remaining / 1000 / 25 * 60;
  const lowMinutes = Math.max(1, Math.round(driveMinutes + stopsAhead * 6));
  const highMinutes = lowMinutes + Math.max(2, Math.round(lowMinutes * 0.4));
  const hhmm = (addMinutes: number) => {
    const d = new Date(Date.now() + addMinutes * 60000);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  return {
    // courier 表只有名字（沒有電話、沒有車牌），假資料也照著給，才不會做出後端給不出來的畫面
    driver: { name: "陳大哥" },
    position: {
      lat,
      lng,
      bearing,
      ts: Date.now(),
      activity: onFoot ? "on_foot" : "in_vehicle",
      isMoving: true,
    },
    eta: { from: hhmm(lowMinutes), to: hhmm(highMinutes) },
    stopsAhead,
  };
};

export const resolveOrderRequest = (config: InternalAxiosRequestConfig): { data: unknown } | null => {
  const u = config.url || "";
  const method = (config.method || "get").toLowerCase();

  // 首頁 / 帳號頁資料（含 AI 預測、平均叫桶、瓦斯行、錢包/優惠券計數）。
  // 依情境回不同狀態：新客戶（掃碼/傳單/首次設定/簡訊邀請）尚無叫桶紀錄 → 冷啟動「尚無紀錄」hero
  // （首次設定另有 needIdentityCheck 驅動的「完成加入設定」橘卡）；其餘（下單/查詢/帳號）給既有客戶資料。
  if (u.includes("/app-api/customer/home")) {
    const scenario = getPracticeScenario();
    const newCustomer =
      scenario === "onboardingQr" ||
      scenario === "flyerKnown" ||
      scenario === "flyerUnknown" ||
      scenario === "firstJoinSetup" ||
      scenario === "smsInvite";
    // 區間預測 v2：HomePage 的 intervalEligible 需要 auto_eligible / j_eligible 或
    // （prediction_source==="survey" 且有真實 last_order）才會顯示區間卡；只給
    // prediction_status 會被判成 noData，練習首頁就看不到新的預測 UI。
    // LINE 到期通知情境改用「快用完」那組，讓首頁與通知卡片的日期一致。
    const gasStatus = newCustomer ? null : scenario === "notifyExpiring" ? practiceGasStatusExpiring() : gasStatusHealthy();
    return {
      data: {
        customer: { customer_name: getPracticeCustomerName(), main_phone: "0912-345-678" },
        // unpaid_bill：首頁／帳戶頁「待付金額」改讀後端加總（09-E）；對齊練習帳戶訂單紀錄裡配送中的 ADO_8804（1,600）
        widgets: { coupons: 2, payment_methods: 1, points: 120, wallet: 2000, unpaid_bill: { total: 1600, order_count: 1 } },
        home_gas_status: gasStatus,
        home_stats: gasStatus
          ? { avg_order_interval_days: 28, last_order_date: gasStatus.last_order_date }
          : { avg_order_interval_days: null, last_order_date: null },
        // 用氣習慣問卷提示（後端權威欄位）。新客戶尚未填 → 首頁直接長出 inline 問卷卡，
        // 與正式流程一致；既有客戶視為已填，不重複打擾。
        survey_prompt:
          newCustomer && !practiceSurveyDone()
            ? { needed: true, reason: "never", completed_at: null }
            : { needed: false, reason: null, completed_at: futureDate(newCustomer ? 0 : -30) },
        // 週期回饋卡：練習沙盒不主動彈出（避免蓋住主要練習動線）。
        cycle_feedback: { should_show: false, kind: null, surface: null, paused: false, cycle_anchor: null },
        vendor: { supplier_name: practiceSupplierName(), phone: practiceSupplierPhone(), is_open_now: true },
      },
    };
  }
  if (u.includes("/app-api/coupons")) {
    // 真後端會依 status 過濾（結帳的自動帶券就是打 status=available）。
    // 練習模式原本一律回全部，於是自動帶券在「已用 / 已過期」裡挑到面額最大的那張，
    // 畫面顯示「折 $100」但算費不會扣 —— 金額對不起來，正是練習模式最不該給的印象。
    const wantStatus = (config.params as Record<string, unknown> | undefined)?.status;
    const items = practiceCoupons();
    return { data: { items: wantStatus ? items.filter((c) => c.status === wantStatus) : items } };
  }
  if (u.includes("/delivery/address/location/")) {
    return { data: INSTALL_LOCATIONS };
  }
  // QuickOrder 畫面用的可預約時段 / 草稿清單（不在練習情境內，但仍在路由中；
  // 沒攔會掉進空 fallthrough 讓畫面空白或報錯，故一併給合理假資料）。
  if (u.includes("/app-api/business-hours/available-slots")) {
    return { data: deliverySlots() };
  }
  if (u.includes("/app-api/quick-order/draft/line-user")) {
    return { data: { draft_id_list: [] } };
  }

  // bootstrap / current
  if (u.includes("/platform-order-drafts/bootstrap")) {
    return { data: buildBootstrap() };
  }
  if (u.includes("/platform-order-drafts/current")) {
    ensureOrderSeed(); // 瓦斯訂購情境保底：草稿空了就補回示範品項，避免購物車頁迴圈
    return { data: { draft: gasList.length || commodityList.length ? buildDraft() : null } };
  }

  // 算費
  if (u.includes("/platform-order-drafts/") && u.includes("/preview")) {
    const b = parseBody(config);
    if ("customer_coupon_id" in b) draftMeta.customer_coupon_id = b.customer_coupon_id;
    return { data: buildPreview() };
  }

  // 品項 新增/更新/刪除
  if (u.includes("/gas-items") || u.includes("/commodity-items")) {
    const type: "gas" | "commodity" = u.includes("/gas-items") ? "gas" : "commodity";
    if (method === "post") addItem(type, parseBody(config));
    else if (method === "put") updateItem(type, lastSeg(u), parseBody(config));
    else if (method === "delete") delItem(type, lastSeg(u));
    return { data: { draft: buildDraft() } };
  }

  // 草稿 建立/取得/更新/刪除
  if (u.includes("/platform-order-drafts")) {
    if (method === "post") {
      const b = parseBody(config);
      if (Array.isArray(b.gas_list)) {
        gasList = [];
        b.gas_list.forEach((it: Record<string, any>) => addItem("gas", it));
      }
      if (Array.isArray(b.commodity_list)) {
        commodityList = [];
        b.commodity_list.forEach((it: Record<string, any>) => addItem("commodity", it));
      }
      persistOrderState();
      return { data: { draft_id: DRAFT_ID, draft: buildDraft() } };
    }
    if (method === "put") {
      draftMeta = { ...draftMeta, ...parseBody(config) };
      persistOrderState();
      // 與 GET 同理，draft 要「平鋪」在 data：真實 API 的 PUT /platform-order-drafts/:id
      // 走 ResponseSerializerInterceptor(DraftResDto)，draft_id 就在 data 頂層。
      // 包成 { draft } 會讓 CouponSwitchMenu 的 syncDraft(res.data.data) 收到沒有 draft_id
      // 的物件 → draftOrderId 變 undefined、productList 清空，確認訂單頁的「訂購項目」
      // 與「訂單摘要」整片空白（自動帶入最佳優惠券會在進頁時就觸發，不必手動選券）。
      return { data: buildDraft() };
    }
    if (method === "delete") {
      resetPracticeOrder();
      return { data: { success: true } };
    }
    // GET 草稿詳情：cart.fetchDraftDetail 直接取 res.data.data 當 draft 丟給 syncDraft，
    // 故這裡 draft 要「平鋪」在 data，不能再包一層 { draft }。包錯會讓 syncDraft 收到
    // 沒有 draft_id / gas_list 的物件 → draftOrderId=undefined、productList=[]，購物車被清空
    // → 結帳頁因空車把使用者踢回「瓦斯訂購」。（與 /current、item endpoints 的 {draft} 包法不同）
    ensureOrderSeed(); // 瓦斯訂購情境保底：草稿空了就補回示範品項，避免購物車頁迴圈
    return { data: buildDraft() };
  }

  // 配送員即時定位（GET /platform-orders/:id/tracking）— 須排在訂單詳情之前，
  // 否則網址前綴相同會先被詳情那條吃掉。
  if (u.includes("/app-api/platform-orders/") && u.endsWith("/tracking") && method === "get") {
    return { data: buildTracking() };
  }

  // 訂單詳情（GET /platform-orders/:id）— 須在送出(POST)之前判斷
  if (u.includes("/app-api/platform-orders/") && method === "get") {
    const isJustSubmitted = u.includes(ORDER_ID);
    return { data: buildOrderDetail(isJustSubmitted ? submittedSnapshot : null) };
  }

  // 送出訂單（POST /platform-orders）
  if (u.includes("/app-api/platform-orders") && method === "post") {
    // 先把「送出的是什麼」凍結起來，供完成頁的 GET /platform-orders/:id 使用（草稿隨後會被清空）
    submittedSnapshot = buildPreview();
    submittedItems = [...gasList, ...commodityList];
    submittedDelivery = {
      mode: (draftMeta.delivery_mode as string) ?? null,
      date: (draftMeta.delivery_date as string) ?? null,
      slotId: (draftMeta.delivery_slot_id as number) ?? null,
    };
    return {
      data: {
        platform_order_id: ORDER_ID,
        order_number: ORDER_NO,
        supplier_order_snapshot_json: { order_list: { order_id: ORDER_NO } },
      },
    };
  }

  // 訂單列表（GET /platform-orders，無 id）— 須在訂單詳情(帶 id)之後判斷
  if (u.includes("/app-api/platform-orders") && method === "get") {
    return { data: { items: buildOrderList() } };
  }

  return null;
};
